import { InformationCircleIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import cartUtils from '../../../../utils/cart';
import { StyledCheck } from '../../unsubscribe/Checkbox';
import CheckoutPaymentButton from './CheckoutPaymentButton';
import CheckoutSummaryLineItem from './CheckoutSummaryLineItem';
import PicsPlusModal from './PicsPlusModal';
import PicsPlusMemberModal from './PicsPlusMemberModal';
import { logPicsPlusInteraction } from '../../../../actions/v3Order';

const SummaryBox = styled.div`
  border: 1px solid #ccc;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-top: 0;
  text-align: center;
`;

const SummaryHeader = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 24px;
`;

const PicsPlusBanner = styled.div`
  background: linear-gradient(135deg, #ff6b9d 0%, #ffa06b 100%);
  margin-bottom: -12px;
  margin-left: -24px;
  margin-right: -24px;
  margin-top: 24px;
  color: white;
  padding: 12px 16px 18px 16px;
  text-align: left;
  display: flex;
  position: relative;
  border-radius: 8px;
  animation: gentlePulse 3s ease-in-out infinite;
  box-shadow: 0 4px 16px rgba(255, 107, 157, 0.25);

  @keyframes gentlePulse {
    0%,
    100% {
      box-shadow: 0 4px 16px rgba(255, 107, 157, 0.25);
    }
    50% {
      box-shadow: 0 6px 24px rgba(255, 107, 157, 0.4),
        0 0 40px rgba(255, 160, 107, 0.2);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const InfoButton = styled(InformationCircleIcon)`
  width: 16px;
  height: 16px;
  cursor: help;
  color: #999;
  margin-left: 4px;
  &:hover {
    color: #666;
  }
`;

const ServiceFeeText = styled.p`
  margin: 0;
  span {
    font-weight: 700;
  }
`;

const PriceLabel = styled.div`
  display: flex;
  align-items: center;
`;

const TooltipWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const TooltipContent = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc(100% + 8px);
  background-color: #333;
  color: #fff;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: normal;
  line-height: 1.4;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  width: 280px;
  text-align: center;
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #333;
  }
`;

const StyledCheckbox = styled(StyledCheck)`
  margin-right: 5px;
  border-radius: 5px;
  border: none;
  background: white;
  cursor: pointer;
`;

const LinkStyled = styled(Link)`
  text-decoration: underline;
  color: #479de6;
  cursor: pointer;
  margin-top: 16px;
`;

const InfoTooltip = ({ text }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <TooltipWrapper
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <InfoButton />
      {showTooltip && <TooltipContent>{text}</TooltipContent>}
    </TooltipWrapper>
  );
};

const CheckoutSummary = ({
  cartTotals,
  discountCodes,
  handleSubmit,
  submittingOrder,
}) => {
  const dispatch = useDispatch();
  const {
    offerPicsPlus,
    isPicsPlusMember,
    picsPlusSubscriptionInfo,
    picsPlusExperiment,
    subscriberCount,
    uuid,
  } = useSelector((state) => state.v3Order);
  const needsConfirmation = useSelector(
    (state) => state.parent.needsConfirmation,
  );
  const [open, setOpen] = useState(false);
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [checked, setChecked] = useState(() => {
    if (uuid) {
      return localStorage.getItem(`picsPlusAccepted_${uuid}`) === 'true';
    }
    return false;
  });

  const handleCheck = () => {
    const newCheckedState = !checked;
    setChecked(newCheckedState);
    if (uuid) {
      localStorage.setItem(
        `picsPlusAccepted_${uuid}`,
        newCheckedState.toString(),
      );
    }
    dispatch(
      logPicsPlusInteraction(
        newCheckedState
          ? 'banner_checkbox_checked'
          : 'banner_checkbox_unchecked',
      ),
    );
  };

  // Initialize checkbox state from localStorage when uuid becomes available
  useEffect(() => {
    if (uuid) {
      const savedState = localStorage.getItem(`picsPlusAccepted_${uuid}`);
      if (savedState === 'true') {
        setChecked(true);
      }
    }
  }, [uuid]);

  // Auto-show modal once per cart session if they haven't seen it
  useEffect(() => {
    if (
      offerPicsPlus &&
      uuid &&
      !localStorage.getItem(`picsPlusModalShown_${uuid}`)
    ) {
      setOpen(true);
      dispatch(logPicsPlusInteraction('modal_view', 'auto'));
    }
  }, [offerPicsPlus, uuid, dispatch]);

  const closeModalWithoutUnchecking = () => {
    setOpen(false);
  };
  const closeModal = () => {
    setOpen(false);
    setChecked(false);
    if (uuid) {
      localStorage.setItem(`picsPlusModalShown_${uuid}`, 'true');
      localStorage.setItem(`picsPlusAccepted_${uuid}`, 'false');
    }
    dispatch(logPicsPlusInteraction('modal_decline'));
  };
  const openModal = () => {
    setOpen(true);
  };
  const handleInfoButtonClick = () => {
    dispatch(logPicsPlusInteraction('info_button_click'));
    dispatch(logPicsPlusInteraction('modal_view', 'info_button'));
    setOpen(true);
  };
  const handleBannerCtaClick = () => {
    dispatch(logPicsPlusInteraction('banner_cta_click'));
    dispatch(logPicsPlusInteraction('modal_view', 'banner_cta'));
    setOpen(true);
  };
  const handleModalSubmit = () => {
    setChecked(true);
    setOpen(false);
    if (uuid) {
      localStorage.setItem(`picsPlusModalShown_${uuid}`, 'true');
      localStorage.setItem(`picsPlusAccepted_${uuid}`, 'true');
    }
    dispatch(logPicsPlusInteraction('modal_accept'));
  };
  // const handleSubmitWithPicsPlus = () => {
  //   setChecked(true);
  //   handleSubmit(true);
  // };
  const handleCheckout = () => {
    handleSubmit(checked, offerPicsPlus);
  };

  const containsFreeShippingDiscount =
    discountCodes?.some((discountCode) => {
      return discountCode.is_free_shipping;
    }) || false;

  return (
    <SummaryBox>
      <SummaryHeader>Order Summary</SummaryHeader>
      <CheckoutSummaryLineItem
        label="Products Subtotal"
        price={cartTotals['total']}
      />
      {/* Only show shipping if greater than 0 */}
      {cartUtils.cartHasShippingCost(cartTotals) && (
        <CheckoutSummaryLineItem
          label="Shipping"
          showFreeIfDiscount={containsFreeShippingDiscount}
          price={cartTotals['shipping']}
        />
      )}
      <CheckoutSummaryLineItem label="Tax" price={cartTotals['tax']} />
      {cartTotals['serviceFee'] > 0 && (
        <CheckoutSummaryLineItem
          label={
            cartTotals['serviceFeeDescription'] ? (
              <PriceLabel>
                Service Fee{' '}
                <InfoTooltip text={cartTotals['serviceFeeDescription']} />
              </PriceLabel>
            ) : (
              'Service Fee'
            )
          }
          price={cartTotals['serviceFee']}
        />
      )}
      <CheckoutSummaryLineItem
        hideIfZero={true}
        label="Discount"
        price={cartTotals['discount']}
      />
      <CheckoutSummaryLineItem
        hideIfZero={true}
        label="Family Discount"
        price={cartTotals['familyDiscount']}
      />
      {cartTotals['voucherAppliedCents'] > 0 && (
        <CheckoutSummaryLineItem
          label="Voucher Credit"
          price={-cartTotals['voucherAppliedCents']}
        />
      )}
      {cartTotals['voucherPurchaseSubtotalCents'] > 0 && (
        <CheckoutSummaryLineItem
          label="Vouchers"
          price={cartTotals['voucherPurchaseSubtotalCents']}
        />
      )}
      {offerPicsPlus && (
        <CheckoutSummaryLineItem
          label={
            <PriceLabel>
              Pics Plus Fee <InfoButton onClick={handleInfoButtonClick} />
            </PriceLabel>
          }
          price={0}
          showZeroIfZero={true}
        />
      )}
      <CheckoutSummaryLineItem
        label="Grand Total"
        price={cartTotals['grandTotal']}
        showZeroIfZero={true}
      />
      {needsConfirmation ? (
        <>
          <div className="alert alert-warning">
            Please confirm email before ordering
            <br />
            <LinkStyled to="/v3/checkout/customer-email-edit">
              Resend confirmation
            </LinkStyled>
          </div>
        </>
      ) : (
        <CheckoutPaymentButton
          grandTotal={cartTotals['grandTotal']}
          handleSubmit={handleCheckout}
          submittingOrder={submittingOrder}
        />
      )}
      {/* <CheckoutPaymentButton
        grandTotal={cartTotals['grandTotal']}
        handleSubmit={() => handleSubmit(false)}
        submittingOrder={submittingOrder}
      /> */}
      {isPicsPlusMember && (
        <PicsPlusBanner>
          <ServiceFeeText style={{ flex: 1 }}>
            🎉 <strong>Welcome back, Pics+ Member!</strong>
            <br />
            <span style={{ fontSize: '11px' }}>
              Enjoy zero fees on this order •{' '}
              <span
                onClick={() => setMemberModalOpen(true)}
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                See what else you get
              </span>
            </span>
          </ServiceFeeText>
        </PicsPlusBanner>
      )}
      {offerPicsPlus && !isPicsPlusMember && (
        <PicsPlusBanner>
          <StyledCheckbox
            checked={checked}
            onClick={handleCheck}
            stroke="#0faaa4ff"
          />
          <ServiceFeeText>
            Sign me up for a free trial of{' '}
            <span
              onClick={handleBannerCtaClick}
              style={{
                textDecoration: 'underline',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Pics+
            </span>{' '}
            so I can access{' '}
            <span
              onClick={handleBannerCtaClick}
              style={{
                textDecoration: 'underline',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              exclusive perks and deals!
            </span>
          </ServiceFeeText>
        </PicsPlusBanner>
      )}
      <PicsPlusModal
        open={open}
        onClose={closeModalWithoutUnchecking}
        onDecline={closeModal}
        onSubmit={handleModalSubmit}
        subscriberCount={subscriberCount}
        experimentConfig={picsPlusExperiment}
      />
      <PicsPlusMemberModal
        open={memberModalOpen}
        onClose={() => setMemberModalOpen(false)}
        subscriptionInfo={picsPlusSubscriptionInfo}
      />
    </SummaryBox>
  );
};

export default CheckoutSummary;
